/**
 * Dashboard State Hook
 *
 * Fetches user lifecycle stage and overlays from /user/state endpoint.
 * Single source of truth for dashboard personalization - shared by web & iOS.
 */

import { useUserState, LifecycleStage, DashboardOverlay } from "@moonscape/shared";
import { DashboardContext, DashboardMetrics } from "../layouts";

// Re-export types for backward compatibility
export { LifecycleStage, DashboardOverlay };

export function useDashboardState(): DashboardContext & { isLoading: boolean } {
  const { data, isLoading } = useUserState();

  // Map server response to DashboardContext
  // Falls back to NEW stage while loading
  const stage = data?.stage ?? LifecycleStage.NEW;
  const overlays = data?.overlays ?? [];
  const isPremium = data?.isPremium ?? false;

  // Map server metrics to DashboardMetrics interface
  const metrics: DashboardMetrics = {
    txCount: data?.metrics?.txCount ?? 0,
    chainCount: data?.metrics?.chainCount ?? 0,
    hasTaxSettings: data?.metrics?.hasTaxSettings ?? false,
    brokenConnections: data?.metrics?.brokenConnections ?? 0,
    bridgePairsUnmatched: data?.metrics?.bridgePairsUnmatched ?? 0,
    auditFlags: data?.metrics?.auditFlags ?? 0,
    daysToDeadline: data?.metrics?.daysToDeadline ?? null,
    pricedPct: data?.metrics?.pricedPct ?? 1.0,
    freeTxnUsed: data?.metrics?.freeTxnUsed ?? 0,
    freeTxnLimit: data?.metrics?.freeTxnLimit ?? 100,
    planSlug: data?.metrics?.planSlug ?? "free",
    planTxnLimit: data?.metrics?.planTxnLimit ?? null,
    nextPlanSlug: data?.metrics?.nextPlanSlug ?? null,
    nextPlanName: data?.metrics?.nextPlanName ?? null,
    isOverPlanLimit: data?.metrics?.isOverPlanLimit ?? false,
    isGrandfathered: data?.metrics?.isGrandfathered ?? false,
    hasExchanges: data?.metrics?.hasExchanges ?? false,
    isSyncing: data?.metrics?.isSyncing ?? false,
  };

  return {
    stage,
    overlays,
    metrics,
    isPremium,
    isLoading,
  };
}
